/**
 * Duration formatting utilities
 * Provides dayjs equivalents for date-fns duration functions
 */
import dayjs, { type Dayjs } from './dayjs-config';
import { toDayjs } from './parse-iso-safe';

export interface DurationComponents {
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
}

/**
 * Calculate the duration between two dates
 * Equivalent to date-fns's intervalToDuration
 *
 * @param start - Start date
 * @param end - End date
 * @returns Duration components
 *
 * @example
 * const start = dayjs('2024-01-15T10:00:00');
 * const end = dayjs('2024-01-15T11:30:00');
 * getDuration(start, end) // { hours: 1, minutes: 30 }
 */
export function getDuration(
  start: Date | Dayjs | string | null | undefined,
  end: Date | Dayjs | string | null | undefined,
): DurationComponents {
  const startDate = toDayjs(start);
  const endDate = toDayjs(end);

  if (!startDate || !endDate) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  const duration = dayjs.duration(endDate.diff(startDate));

  return {
    hours: Math.floor(duration.asHours()),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
    milliseconds: duration.milliseconds(),
  };
}

/**
 * Format a duration in a human-readable format
 * Equivalent to date-fns's formatDuration
 *
 * @param duration - Duration components
 * @param options - Formatting options
 * @returns Formatted duration string
 *
 * @example
 * formatDuration({ hours: 1, minutes: 30 }, { format: ['hours', 'minutes'] })
 * // "1 hour 30 minutes"
 */
export function formatDuration(
  duration: DurationComponents,
  options?: {
    format?: Array<'hours' | 'minutes' | 'seconds'>;
    delimiter?: string;
    zero?: boolean;
  },
): string {
  const { format = ['hours', 'minutes', 'seconds'], delimiter = ' ', zero = false } = options || {};

  const parts: string[] = [];

  if (format.includes('hours')) {
    const hours = duration.hours || 0;
    if (hours > 0 || zero) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
  }

  if (format.includes('minutes')) {
    const minutes = duration.minutes || 0;
    if (minutes > 0 || zero) {
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }
  }

  if (format.includes('seconds')) {
    const seconds = duration.seconds || 0;
    if (seconds > 0 || zero) {
      parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
    }
  }

  return parts.join(delimiter);
}

/**
 * Format duration for card display (e.g., "1h30min")
 * Matches the existing timeFormattingOnCard behavior
 *
 * @param start - Start date
 * @param end - End date
 * @returns Formatted duration string like "1h30min" or "45min"
 */
export function formatDurationForCard(
  start: Date | Dayjs | string | null | undefined,
  end: Date | Dayjs | string | null | undefined,
): string {
  const duration = getDuration(start, end);
  const hours = duration.hours || 0;
  const minutes = duration.minutes || 0;

  if (hours > 0 && minutes > 0) {
    return `${hours}h${minutes}min`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}min`;
  }

  return '0min';
}

/**
 * Translation function type for formatDurationMinutes
 */
export type TranslationFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Format minutes to a localized duration string
 *
 * @param totalMinutes - Total minutes to format
 * @param t - Translation function from useTranslation hook
 * @returns Localized string like "1h 24m" if >= 60 minutes, "24m" otherwise
 *
 * @example
 * formatDurationMinutes(84, t) // "1h 24m" (en-US)
 * formatDurationMinutes(45, t) // "45m" (en-US)
 * formatDurationMinutes(120, t) // "2h" (en-US)
 */
export function formatDurationMinutes(totalMinutes: number, t: TranslationFunction): string {
  const rounded = Math.round(totalMinutes);
  const hours = Math.floor(rounded / 60);
  const remainingMinutes = rounded % 60;

  if (hours > 0 && remainingMinutes > 0) {
    return t('common:time.hoursMinutesShort', { hours, minutes: remainingMinutes });
  } else if (hours > 0) {
    return t('common:time.hoursShort', { time: hours });
  }
  return t('common:time.minutesShort', { time: rounded });
}

/**
 * Get the difference between two dates in a specific unit
 *
 * @param start - Start date
 * @param end - End date
 * @param unit - Unit of measurement
 * @returns Difference in the specified unit
 *
 * @example
 * getDifference(start, end, 'minutes') // 90
 * getDifference(start, end, 'hours') // 1.5
 */
export function getDifference(
  start: Date | Dayjs | string | null | undefined,
  end: Date | Dayjs | string | null | undefined,
  unit: 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days' = 'milliseconds',
): number {
  const startDate = toDayjs(start);
  const endDate = toDayjs(end);

  if (!startDate || !endDate) return 0;

  return endDate.diff(startDate, unit, true); // true for floating point precision
}
